import { fetchWeather, geocode, localizeById, reverseGeocode, searchPlaces, type Place } from "./api/weather";
import { dedupePlaces } from "./core/query";
import { fetchCitiesNow, localTimeOf, type CityNow } from "./api/cities";
import { fetchCandidates, trackUrl, artistUrl, JamendoError } from "./api/jamendo";
import { fetchArchiveTracks } from "./api/archive";
import { fetchPlacePhotos, type Photo } from "./api/photos";
import { toSignals, type Observation } from "./core/signals";
import { driftTags, toProfile, queryTags } from "./core/profile";
import { rank, buildSet, extendSet } from "./core/rank";
import { artistFatigueIndex, fatigueIndex, heardCount, verdict } from "./core/history";
import { allPlays, recordPlay } from "./lib/plays";
import { loadTaste, saveTaste } from "./lib/taste";
import { learn } from "./core/taste";
import { makeRng, seedFrom } from "./core/random";
import { toSentence } from "./core/explain";
import { LANGS, LANG_NAMES, detectLang, fmt, loadDict, setLang, t, type Lang } from "./core/i18n";
import { CITIES, presetName } from "./core/cities";
import { contrast, skyIcon, sparkline } from "./core/sky";
import { ICONS, icon } from "./ui/icons";
import { applyTheme, getTheme, nextTheme, type Theme } from "./ui/theme";
import { moodHue } from "./ui/mood";
import { toScene } from "./core/scene";
import { WeatherFx } from "./ui/fx";
import type { Profile, Scored, Signal } from "./core/types";

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const el = {
  bg: $("bg"), photo: $("photo"), fx: $("fx"), fxCanvas: $<HTMLCanvasElement>("fx-canvas"),
  wxIc: $("wx-ic"), wxT: $("wx-t"), wxCity: $("wx-city"), wxSub: $("wx-sub"),
  sentence: $("sentence"), facts: $("facts"),
  art: $<HTMLImageElement>("art"), tname: $("tname"), tartist: $("tartist"),
  credit: $<HTMLAnchorElement>("credit"),
  bar: $("bar"), fill: $("fill"), tnow: $("tnow"), tlen: $("tlen"),
  prev: $<HTMLButtonElement>("prev"), play: $<HTMLButtonElement>("play"), next: $<HTMLButtonElement>("next"),
  mute: $<HTMLButtonElement>("mute"), vol: $<HTMLInputElement>("vol"),
  queue: $("queue"), cities: $("cities"), citiesNote: $("cities-note"),
  q: $<HTMLInputElement>("q"), go: $<HTMLButtonElement>("go"), here: $<HTMLButtonElement>("here"),
  findOut: $("find-out"), findMsg: $("find-msg"), sugg: $("sugg"),
  theme: $<HTMLButtonElement>("theme"), lang: $<HTMLSelectElement>("lang"), langIc: $("lang-ic"), langCode: $("lang-code"),
  hNext: $("h-next"), hCities: $("h-cities"), stat: $("stat"),
  notice: $("notice"), err: $("err"),
  install: $("install"), installIc: $("install-ic"), installText: $("install-text"),
  installYes: $<HTMLButtonElement>("install-yes"), installNo: $<HTMLButtonElement>("install-no"),
  credits: $("credits"), brandIc: $("brand-ic"), findIc: $("find-ic"),
  audio: $<HTMLAudioElement>("audio"),
};

const fx = new WeatherFx(el.fxCanvas, el.fx);
// Погоду не закажешь: дождь и снег в нужный момент просто не идут. В сборку
// не попадает — Vite подставляет false и вырезает ветку целиком.
// Имя с подчёркиваниями не случайно: window.fx уже занят самим <div id="fx">
// через именованный доступ к элементам, и на этой подмене легко обмануться.
if (import.meta.env.DEV) (window as unknown as { __fx: WeatherFx }).__fx = fx;

const HOME = CITIES[0]!;
const REFILL_AT = 3;
const RECHECK_MS = 10 * 60 * 1000;
/** Пауза после неудачной доливки. Короткая: ждать нечего, выдача просто
 *  бывает пустой, и следующая попытка независима. */
const REFILL_COOLDOWN_MS = 8_000;
const RETRY_DELAYS = [20_000, 45_000, 90_000];

let lang: Lang = detectLang();
let theme: Theme = getTheme();
let place: Place = { name: HOME.ru, lat: HOME.lat, lon: HOME.lon, nameLang: "ru" };
let signals: Signal[] = [];
let profile: Profile | null = null;
let lastObs: Observation | null = null;
let set: Scored[] = [];
let idx = 0;
let drift = 0;
let refilling = false;
let refillBlockedUntil = 0;
let retryAt = 0;
let retryTimer: number | undefined;
let cityData: CityNow[] = [];
let photoTimer: number | undefined;

const esc = (s: string) => s.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c]!));
const fail = (m: string) => { el.err.textContent = m; el.err.hidden = false; };

/** Ошибка на языке интерфейса, а не на том, на котором её написали в коде. */
function errorText(e: unknown): string {
  const d = t(lang);
  if (e instanceof JamendoError) {
    return e.code === "empty" ? d.errEmpty
      : e.code === "rate_limited" ? d.errThrottled
      : e.code === "no_key" ? d.errNoKey
      : e.code === "api" ? d.errApi : d.errHttp;
  }
  return d.failed;
}
const mmss = (s: number) => Number.isFinite(s) && s >= 0
  ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : "--:--";

let noticeTimer: number | undefined;
/** Короткая строка, которая сама исчезает: объяснение, а не ошибка. */
function notice(m: string) {
  el.notice.textContent = m;
  el.notice.hidden = false;
  if (noticeTimer) clearTimeout(noticeTimer);
  noticeTimer = window.setTimeout(() => { el.notice.hidden = true; }, 7000);
}

/* ───────────────────────── тема и язык ───────────────────────── */

function paintTheme() {
  applyTheme(theme);
  el.theme.innerHTML = icon(theme === "light" ? "sun" : theme === "dark" ? "moon" : "theme");
  el.theme.title = theme === "light" ? t(lang).themeLight
    : theme === "dark" ? t(lang).themeDark : t(lang).themeSystem;
}
el.theme.addEventListener("click", () => { theme = nextTheme(theme); paintTheme(); });

function paintLang() {
  const d = t(lang);
  document.documentElement.lang = lang;
  el.q.placeholder = d.city;
  el.go.textContent = d.pick;
    el.here.title = d.here;
  el.prev.setAttribute("aria-label", d.prevTrack);
  el.next.setAttribute("aria-label", d.nextTrack);
  el.vol.setAttribute("aria-label", d.volume);
  el.mute.setAttribute("aria-label", d.volume);
  if (!el.lang.options.length) {
    el.lang.innerHTML = LANGS
      .map((l) => `<option value="${l}">${esc(LANG_NAMES[l])}</option>`)
      .join("");
  }
  el.lang.value = lang;
  // Ключи словаря и есть двухбуквенные коды ISO — отдельная таблица не нужна.
  el.langCode.textContent = lang.toUpperCase();
  el.hNext.textContent = d.queue;
  el.hCities.textContent = d.cities;
  el.citiesNote.textContent = d.citiesNote;
  el.credits.textContent = d.credits + " ";
  el.installText.innerHTML = `<b>${esc(d.installTitle)}</b> — ${esc(d.installWhy)}`;
  el.installYes.textContent = d.installYes;
  el.installNo.textContent = d.installNo;
  paintHeard();
  paintTheme();
}

/**
 * Имя места на языке интерфейса.
 *
 * Имя хранится готовой строкой, поэтому при смене языка оставалось прежним:
 * «Кейптаун, ЮАР, 6 grey days in a row» — половина фразы на одном языке,
 * половина на другом. Раньше переставлялся только HOME, и только между ru и en.
 *
 * Три источника, по убыванию точности: список ленты (без сети), тот же
 * геокодинг по id, обратный геокодер как последняя зацепка для мест, у
 * которых id нет (геолокация и находки OSM). Если ничего не вышло — молча
 * оставляем прежнее имя: смешанный язык лучше пустого места.
 */
let localizeSeq = 0;

async function localizePlace() {
  // Место, сохранённое до появления nameLang, своего языка не знает.
  // Переразрешать его вслепую нельзя: id у него тоже нет, а обратный
  // геокодер превратил бы «Кишинёв» в «город Кишинёв».
  if (!place.nameLang || place.nameLang === lang) return;
  const seq = ++localizeSeq;
  const was = place.name;

  const preset = presetName(place.lat, place.lon, lang);
  const fresh = preset
    ?? (place.id != null ? await localizeById(place.id, lang).catch(() => null) : null)
    ?? (place.id == null ? await reverseGeocode(place.lat, place.lon, lang).catch(() => null) : null);

  // Пока ходили в сеть, язык могли переключить ещё раз или сменить город.
  if (seq !== localizeSeq || place.name !== was) return;
  if (!fresh) return;

  place = { ...place, name: fresh, nameLang: lang };
  rememberPlace(place);
  el.wxCity.textContent = place.name;
  // Поле показывает имя места после поиска — но только если его не начали
  // править руками.
  if (el.q.value === was) el.q.value = place.name;
  if (lastObs) el.sentence.textContent = toSentence(place.name, signals, lang);
}

el.lang.addEventListener("change", async () => {
  const next = el.lang.value as Lang;
  if (!LANGS.includes(next)) return;
  lang = next;
  setLang(lang);
  await loadDict(lang);
  paintLang();
  if (lastObs) paintWeather(lastObs);
  renderCities();
  // Интерфейс переводится сразу, имя города — когда приедет ответ.
  void localizePlace();
});

/* ───────────────────────── погодный виджет ───────────────────────── */

const one = (v: number) => (Math.round(v * 10) / 10).toString();

function paintWeather(o: Observation) {
  const d = t(lang);
  el.wxIc.innerHTML = ICONS[skyIcon(o.code, o.isDay)] ?? "";
  el.wxT.textContent = `${Math.round(o.temp)}°`;
  el.wxCity.textContent = place.name;
  el.wxSub.textContent = `${d.feels} ${Math.round(o.apparent)}°`;
  el.sentence.textContent = toSentence(place.name, signals, lang);

  // Показания, по которым и подбиралась музыка — в том же порядке важности.
  const facts: [string, string][] = [
    ["wind", `${d.wind} ${Math.round(o.wind)} ${d.kmh}`],
    ["cloudy", `${d.cloud} ${Math.round(o.cloud)}%`],
  ];
  if (o.precip > 0) facts.unshift(["drop", `${d.precip} ${one(o.precip)} ${d.mmh}`]);
  facts.push(["gauge", `${d.pressure} ${Math.round(o.pressure)} ${d.hpa}`]);

  el.facts.innerHTML = facts
    .map(([ic, txt]) => `<span class="fact">${icon(ic)}${esc(txt)}</span>`)
    .join("");

  const { hue, sat } = moodHue(o.temp, o.cloud);
  el.bg.style.setProperty("--h", String(hue));
  el.bg.style.setProperty("--s", `${sat}%`);
  fx.apply(toScene(signals, o));
}

function paintPhotos(list: Photo[]) {
  if (photoTimer) clearInterval(photoTimer);
  if (!list.length) {
    document.body.classList.remove("has-photo");
    el.photo.classList.remove("on");
    el.credit.textContent = "";
    return;
  }
  let i = 0;
  const show = () => {
    const p = list[i % list.length]!;
    const img = new Image();
    img.onload = () => {
      el.photo.style.backgroundImage = `url("${p.url}")`;
      el.photo.classList.add("on");
      document.body.classList.add("has-photo");
      el.credit.textContent = `${p.title} · ${p.author} · ${p.license}`;
      el.credit.href = p.pageUrl;
    };
    img.src = p.url;
    i++;
  };
  show();
  photoTimer = window.setInterval(show, 20000);
}

/* ───────────────────────── плеер ───────────────────────── */

function syncPlay() {
  const on = !el.audio.paused && !el.audio.ended;
  el.play.dataset.playing = on ? "1" : "0";
  el.play.innerHTML = icon(on ? "pause" : "play");
  el.play.setAttribute("aria-label", on ? t(lang).pause : t(lang).play);
  document.body.classList.toggle("playing", on);
  renderQueue();
}

function syncNav() { el.prev.disabled = idx <= 0 && el.audio.currentTime < 3; }

function syncProgress() {
  const { currentTime: c, duration: dur } = el.audio;
  const pct = dur > 0 ? (c / dur) * 100 : 0;
  el.fill.style.width = `${pct}%`;
  el.tnow.textContent = mmss(c);
  el.tlen.textContent = mmss(dur);
  el.bar.setAttribute("aria-valuenow", String(Math.round(pct)));
}

/** Память считается на каждый подбор: история могла пополниться. */
const memory = () => {
  const plays = allPlays();
  return {
    fatigue: fatigueIndex(plays),
    artists: artistFatigueIndex(plays),
    taste: loadTaste(),
    now: Date.now(),
  };
};

/**
 * Номер сессии подбора. Живёт между запусками, поэтому завтрашний подбор в
 * той же погоде смотрит в другое окно каталога, а не повторяет вчерашний.
 * Ноль — первый подбор на этом устройстве: начало выдачи, лучшее сразу.
 */
const ROTATION_KEY = "pw.rotation";

function nextRotation(): number {
  let n = 0;
  try { n = Number(localStorage.getItem(ROTATION_KEY)) || 0; } catch { /* приватный режим */ }
  try { localStorage.setItem(ROTATION_KEY, String(n + 1)); } catch { /* квота */ }
  return n;
}

let rotation = 0;

/**
 * Насколько выбор в наборе отпускается от строгого максимума.
 *
 * Ноль означал бы, что одинаковый пул всегда даёт одинаковый набор — стенд
 * показывал ровно это: пересечение наборов для одной погоды 100%. Значение
 * подобрано по стенду (src/core/eval.test.ts): разнообразие между сессиями
 * появляется, а среднее попадание в профиль почти не проседает.
 */
const PICK_TEMPERATURE = 0.08;

/**
 * Порог попадания для добавки из второго пула. Ниже — в очередь не идёт:
 * лучше короткая очередь про эту погоду, чем длинная про никакую.
 */
const MIN_ENRICH_FIT = 0.12;

/** Своя случайность на каждый подбор — но воспроизводимая внутри него. */
const pickOpts = () => ({
  rng: makeRng(seedFrom(`${place.name}|${rotation}`)),
  temperature: PICK_TEMPERATURE,
});

/** Трек, который сейчас в плеере, и начинал ли он вообще звучать. */
let current: Scored["track"] | null = null;
let started = false;

/**
 * Записать вердикт по уходящему треку.
 *
 * Момент выбран не случайно: только при уходе видно, дослушали его или
 * пролистнули. Трек, который так и не зазвучал (сеть подвела, браузер не дал
 * автоплей), не записывается вовсе — иначе чужая проблема выглядела бы как
 * «не понравилось» и портила подбор.
 */
function closeCurrent() {
  if (!current || !started) return;
  const played = el.audio.currentTime;
  const total = el.audio.duration || current.duration || 0;
  const kind = verdict(played, total);
  recordPlay(current.id, kind, current.artist_id);
  // Вкус учится на тегах: id трека в каталоге на сотни тысяч вещей не
  // повторится, а теги повторяются.
  const m = current.musicinfo?.tags;
  const tags = [...(m?.genres ?? []), ...(m?.instruments ?? []), ...(m?.vartags ?? [])];
  if (tags.length) saveTaste(learn(loadTaste(), tags, kind, Date.now()));
  current = null;
  started = false;
  paintHeard();
}

function paintHeard() {
  const n = heardCount(allPlays());
  el.stat.textContent = n > 0
    ? `${t(lang).contUp} · ${fmt(t(lang).heard, n)}`
    : t(lang).contUp;
}

function renderTrack(s: Scored, autoplay: boolean) {
  closeCurrent();
  current = s.track;
  const tr = s.track;
  el.art.src = tr.album_image || tr.image || "";
  el.art.alt = tr.album_name ?? "";
  el.tname.innerHTML = `<a href="${trackUrl(tr)}" target="_blank" rel="noopener">${esc(tr.name)}</a>`;
  // У архива лицензия своя у каждой записи, поэтому показываем её рядом с
  // исполнителем — это требование CC, а не украшение.
  const badge = tr.source === "archive"
    ? ` <span class="src">Archive · ${esc(tr.licence ?? "CC")}</span>`
    : "";
  el.tartist.innerHTML =
    `<a href="${artistUrl(tr)}" target="_blank" rel="noopener">${esc(tr.artist_name)}</a>${badge}`;
  el.audio.src = tr.audio;
  el.fill.style.width = "0%";
  el.tnow.textContent = "0:00";
  el.tlen.textContent = tr.duration ? mmss(tr.duration) : "--:--";
  if (autoplay) void el.audio.play().catch(() => { /* браузер ждёт жеста */ });
  syncPlay();
  syncNav();

  if ("mediaSession" in navigator) {
    navigator.mediaSession.metadata = new MediaMetadata({
      title: tr.name, artist: tr.artist_name, album: tr.album_name ?? "Play Weather",
      artwork: tr.album_image ? [{ src: tr.album_image, sizes: "600x600", type: "image/jpeg" }] : [],
    });
  }
}

function renderQueue() {
  const rest = set.slice(idx, idx + 16);
  el.queue.innerHTML = rest.map((s, i) => {
    const cur = i === 0;
    const lead = cur && !el.audio.paused
      ? '<span class="eq"><i></i><i></i><i></i></span>'
      : `<span class="n">${idx + i + 1}</span>`;
    // Название и исполнитель на своих строках: в одну они не помещались,
    // и название обрезалось ради имени, которое и так рядом.
    return `<button class="qi" data-i="${idx + i}" aria-current="${cur}">${lead}` +
      `<span class="qtxt"><span class="qt">${esc(s.track.name)}</span>` +
      `<span class="qa">${esc(s.track.artist_name)}</span></span></button>`;
  }).join("");
  el.queue.querySelectorAll<HTMLElement>(".qi").forEach((n) => {
    n.onclick = () => go(Number(n.dataset.i));
  });
}

function go(to: number) {
  if (to < 0 || to >= set.length) return;
  idx = to;
  renderTrack(set[idx]!, true);
  if (set.length - idx <= REFILL_AT) void refill();
}

/**
 * Jamendo при троттлинге молча отдаёт пустую выдачу, поэтому после неудачи
 * ждём минуту — иначе доливка долбит его в цикле.
 */
async function refill(): Promise<boolean> {
  if (!profile || refilling || Date.now() < refillBlockedUntil) return false;
  refilling = true;
  const before = set.length;
  try {
    drift += 2;
    rotation++;
    const more = rank(await fetchCandidates(profile, { drift, rotation }), profile, memory());
    set = extendSet(set, buildSet(more, 12, pickOpts()), 8);
    enrichFromArchive(profile, driftTags(profile, drift, 3));
    renderQueue();
  } catch { /* доиграем что есть */ }
  finally { refilling = false; }

  const grew = set.length > before;
  if (!grew) refillBlockedUntil = Date.now() + REFILL_COOLDOWN_MS;
  return grew;
}

/**
 * Второй пул подмешивается ПОЗЖЕ, а не вместе с первым.
 *
 * Архиву нужен поиск плюс разворот каждой записи — около пяти запросов и
 * несколько секунд. Ждать их до первого трека значит задержать музыку ради
 * добавки. Поэтому Jamendo играет сразу, а архив догоняет и расширяет очередь,
 * когда приедет. Не приехал — просто меньше выбора, не ошибка.
 */
function enrichFromArchive(p: Profile, tags: string[]) {
  const forProfile = p;
  void fetchArchiveTracks(tags, "enrich", rotation)
    .then((extra) => {
      // Погода могла смениться, пока мы ходили — тогда добавка уже не к месту.
      if (!extra.length || profile !== forProfile || !set.length) return;
      // Добавка проходит только если она про эту погоду: раньше архив
      // дописывался безусловно и размывал очередь тем, ради чего весь
      // подбор и затевался.
      set = extendSet(set, buildSet(rank(extra, forProfile, memory()), 6, pickOpts()), 4,
        { minFit: MIN_ENRICH_FIT });
      renderQueue();
    })
    .catch(() => { /* добавка не обязательна */ });
}

/** Не удалось долить — играем уже полученное по кругу: тишина хуже повтора. */
async function advance() {
  if (idx + 1 < set.length) { go(idx + 1); return; }
  if (await refill()) { go(idx + 1); return; }
  if (set.length > 1) { notice(t(lang).looping); go(0); }
}

el.play.addEventListener("click", () => {
  if (el.audio.paused) void el.audio.play().catch(() => {});
  else el.audio.pause();
});
el.prev.addEventListener("click", () => {
  if (el.audio.currentTime > 3 || idx === 0) { el.audio.currentTime = 0; return; }
  go(idx - 1);
});
el.next.addEventListener("click", () => void advance());

function seek(x: number) {
  const r = el.bar.getBoundingClientRect();
  const ratio = Math.min(1, Math.max(0, (x - r.left) / r.width));
  if (el.audio.duration > 0) el.audio.currentTime = ratio * el.audio.duration;
}
el.bar.addEventListener("pointerdown", (e) => { el.bar.setPointerCapture(e.pointerId); seek(e.clientX); });
el.bar.addEventListener("pointermove", (e) => { if (el.bar.hasPointerCapture(e.pointerId)) seek(e.clientX); });
el.bar.addEventListener("keydown", (e) => {
  const step = e.key === "ArrowRight" ? 5 : e.key === "ArrowLeft" ? -5 : 0;
  if (!step || !(el.audio.duration > 0)) return;
  e.preventDefault();
  el.audio.currentTime = Math.min(el.audio.duration, Math.max(0, el.audio.currentTime + step));
});

function paintVol() {
  const v = el.audio.muted ? 0 : el.audio.volume;
  el.vol.value = String(Math.round(v * 100));
  el.vol.style.setProperty("--p", `${Math.round(v * 100)}%`);
  el.mute.innerHTML = icon(v === 0 ? "mute" : "volume");
}
el.vol.addEventListener("input", () => {
  el.audio.volume = Number(el.vol.value) / 100;
  el.audio.muted = el.audio.volume === 0;
  try { localStorage.setItem("pw.vol", String(el.audio.volume)); } catch { /* приватный режим */ }
  paintVol();
});
el.mute.addEventListener("click", () => { el.audio.muted = !el.audio.muted; paintVol(); });

el.audio.addEventListener("timeupdate", () => { syncProgress(); syncNav(); });
el.audio.addEventListener("loadedmetadata", syncProgress);
el.audio.addEventListener("play", () => { started = true; syncPlay(); });
el.audio.addEventListener("pause", syncPlay);
el.audio.addEventListener("ended", () => void advance());

document.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement || e.target === el.bar) return;
  if (e.code === "Space") { e.preventDefault(); el.play.click(); }
  else if (e.code === "ArrowRight") el.next.click();
  else if (e.code === "ArrowLeft") el.prev.click();
});

if ("mediaSession" in navigator) {
  navigator.mediaSession.setActionHandler("previoustrack", () => el.prev.click());
  navigator.mediaSession.setActionHandler("nexttrack", () => el.next.click());
  navigator.mediaSession.setActionHandler("play", () => el.play.click());
  navigator.mediaSession.setActionHandler("pause", () => el.play.click());
}

/* ───────────────────────── города ───────────────────────── */

function spark(curve: number[]): string {
  const pts = sparkline(curve);
  if (!pts) return "";
  return `<svg class="spark" viewBox="0 0 23 10" preserveAspectRatio="none" aria-hidden="true">` +
    `<polyline points="${pts}" vector-effect="non-scaling-stroke"/></svg>`;
}

function renderCities() {
  if (!cityData.length) return;
  const mine = lastObs
    ? { temp: lastObs.temp, cloud: lastObs.cloud, code: lastObs.code, isDay: lastObs.isDay }
    : null;
  // Порядок не алфавитный: впереди города, где сейчас дальше всего от твоей погоды.
  const list = mine ? [...cityData].sort((a, b) => contrast(mine, b) - contrast(mine, a)) : cityData;

  el.cities.innerHTML = list.slice(0, 18).map((c) => {
    const { hue, sat } = moodHue(c.temp, c.cloud);
    const name = lang === "ru" ? c.city.ru : c.city.en;
    const mark = ((Math.min(40, Math.max(-20, c.temp)) + 20) / 60) * 100;
    return `<button class="ct" data-lat="${c.city.lat}" data-lon="${c.city.lon}" data-n="${esc(name)}"` +
      ` style="--ch:${hue};--cs:${sat}%">` +
      `<span class="row1">${icon(skyIcon(c.code, c.isDay))}<span class="deg">${Math.round(c.temp)}°</span></span>` +
      spark(c.curve) +
      `<span class="rail"><i style="left:${mark.toFixed(1)}%"></i></span>` +
      `<span class="row2"><span class="nm">${esc(name)}</span>` +
      `<span class="tm">${localTimeOf(c.utcOffset)}</span></span></button>`;
  }).join("");

  el.cities.querySelectorAll<HTMLElement>(".ct").forEach((n) => {
    n.onclick = () => {
      el.q.value = n.dataset.n ?? "";
      void run({ name: n.dataset.n ?? "", lat: Number(n.dataset.lat), lon: Number(n.dataset.lon) });
      window.scrollTo({ top: 0, behavior: "smooth" });
    };
  });
}

/* ───────────────────────── основной цикл ───────────────────────── */

function rememberPlace(p: Place) {
  try { localStorage.setItem("pw.place", JSON.stringify(p)); } catch { /* приватный режим */ }
}
function lastPlace(): Place | null {
  try {
    const raw = localStorage.getItem("pw.place");
    if (!raw) return null;
    const p = JSON.parse(raw);
    return typeof p?.lat === "number" && typeof p?.lon === "number" && p.name ? p : null;
  } catch { return null; }
}
const samePlace = (a: Place, b: Place) =>
  Math.abs(a.lat - b.lat) < 0.05 && Math.abs(a.lon - b.lon) < 0.05;

/** Троттлинг проходит сам — пробуем ещё раз, а не оставляем мёртвую страницу. */
function scheduleRetry(p: Place) {
  if (retryAt >= RETRY_DELAYS.length) return;
  const delay = RETRY_DELAYS[retryAt]!;
  retryAt++;
  if (retryTimer) clearTimeout(retryTimer);
  notice(fmt(t(lang).retrying, Math.round(delay / 1000)));
  retryTimer = window.setTimeout(() => void run(p), delay);
}

async function run(p: Place, autoplay = true) {
  el.err.hidden = true;
  closeFind();
  el.go.disabled = true;
  el.go.textContent = t(lang).picking;
  place = p;
  rotation = nextRotation();
  el.wxCity.textContent = p.name;
  el.sentence.textContent = t(lang).reading;
  drift = 0;

  try {
    const obs = await fetchWeather(p);
    lastObs = obs;
    signals = toSignals(obs);
    paintWeather(obs);
    renderCities();
    void fetchPlacePhotos(p.lat, p.lon).then(paintPhotos);

    rememberPlace(p);
    // Восстановленное из прошлой сессии место могло сохраниться на другом
    // языке. Зовём после paintWeather: фраза уже нарисована свежими
    // сигналами этого города, и обновлять её безопасно.
    void localizePlace();
    refillBlockedUntil = 0;
    retryAt = 0;
    if (retryTimer) { clearTimeout(retryTimer); retryTimer = undefined; }

    profile = toProfile(signals);
    const tags = queryTags(profile, 3);

    let candidates: Scored["track"][] = [];
    try {
      candidates = await fetchCandidates(profile, { rotation });
    } catch (e) {
      // Jamendo молчит — второй источник не только для разнообразия: он
      // умеет подменить первый целиком. Музыка важнее, чем то, откуда она.
      candidates = await fetchArchiveTracks(tags, "primary", rotation);
      if (!candidates.length) throw e;
      notice(t(lang).viaArchive);
    }

    set = buildSet(rank(candidates, profile, memory()), 12, pickOpts());
    idx = 0;
    if (!set.length) { fail(t(lang).noCandidates); return; }
    renderTrack(set[0]!, autoplay);
    // Добавка идёт только если основной источник сработал — иначе она уже здесь.
    if (candidates[0]?.source !== "archive") enrichFromArchive(profile, tags);
  } catch (e) {
    fail(errorText(e));
    scheduleRetry(p);
  } finally {
    el.go.disabled = false;
    el.go.textContent = t(lang).pick;
  }
}

/* ───────────────────────── поиск города ───────────────────────── */

/** Пауза перед подсказкой: один запрос на серию нажатий, а не на каждое. */
const SUGGEST_MS = 350;
/** Короче трёх символов префикс возвращает шум и тратит вызов зря. */
const SUGGEST_MIN = 3;

let suggList: Place[] = [];
let suggIdx = -1;
let suggTimer: number | undefined;
/** Номер запроса: пока ждали ответ, могли набрать ещё — старый не должен победить. */
let suggSeq = 0;

function closeFind() {
  suggList = [];
  suggIdx = -1;
  el.sugg.textContent = "";
  el.sugg.hidden = true;
  el.findMsg.hidden = true;
  el.findOut.hidden = true;
  el.q.setAttribute("aria-expanded", "false");
}

function findSay(text: string, bad: boolean) {
  el.findMsg.textContent = text;
  el.findMsg.classList.toggle("bad", bad);
  el.findMsg.hidden = false;
  el.findOut.hidden = false;
}

function paintSuggSel() {
  el.sugg.querySelectorAll("button").forEach((b, i) => {
    b.setAttribute("aria-selected", String(i === suggIdx));
  });
}

function showSugg(places: Place[], headline: string) {
  suggList = places;
  suggIdx = -1;
  el.sugg.innerHTML = places
    .map((p) => `<li role="none"><button type="button" role="option" aria-selected="false">${esc(p.name)}</button></li>`)
    .join("");
  el.sugg.querySelectorAll("button").forEach((b, i) => {
    b.addEventListener("click", () => choose(i));
  });
  el.sugg.hidden = false;
  el.findOut.hidden = false;
  if (headline) findSay(headline, false);
  else el.findMsg.hidden = true;
  el.q.setAttribute("aria-expanded", "true");
}

function choose(i: number) {
  const p = suggList[i];
  if (!p) return;
  el.q.value = p.name;
  closeFind();
  void run(p);
}

/**
 * Подсказка по мере набора спрашивает ТОЛЬКО то, что набрано, — один вызов.
 * Полная лестница вариантов (см. core/query.ts) стоит до четырёх и идёт
 * поэтому лишь по явному действию: Enter или «Слушать».
 */
async function liveSuggest(q: string) {
  const seq = ++suggSeq;
  try {
    const found = dedupePlaces(await geocode(q, lang));
    if (seq !== suggSeq) return;
    if (!found.length) { closeFind(); return; }
    showSugg(found, "");
  } catch {
    // На лету молчим: если геокодинг лежит, это скажется при явном поиске.
  }
}

async function submitSearch() {
  const q = el.q.value.trim();
  if (!q) return;
  if (suggTimer) { clearTimeout(suggTimer); suggTimer = undefined; }

  // Пункт уже выбран стрелками — играем его, а не ищем заново.
  if (suggIdx >= 0 && suggList[suggIdx]) { choose(suggIdx); return; }

  const seq = ++suggSeq;
  try {
    const r = await searchPlaces(q, lang);
    if (seq !== suggSeq) return;
    if (!r.hits.length) { closeFind(); findSay(fmt(t(lang).notFound, q), true); return; }
    // Набрано ровно так, как город называется, и он один — переспрашивать незачем.
    if (r.exact && r.hits.length === 1) { closeFind(); void run(r.hits[0]!); return; }
    // Сработала запасная ступень — показываем, что нашли, и НЕ играем молча:
    // иначе «санкт» уехал бы в Санкт-Галлен без объяснений.
    showSugg(r.hits, r.exact ? "" : t(lang).didYouMean);
  } catch {
    closeFind();
    findSay(t(lang).geocodeDown, true);
  }
}

el.go.addEventListener("click", () => void submitSearch());

el.q.addEventListener("input", () => {
  if (suggTimer) clearTimeout(suggTimer);
  const q = el.q.value.trim();
  if (q.length < SUGGEST_MIN) { closeFind(); return; }
  suggTimer = window.setTimeout(() => void liveSuggest(q), SUGGEST_MS);
});

el.q.addEventListener("keydown", (e) => {
  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
    if (!suggList.length) return;
    e.preventDefault();
    suggIdx = (suggIdx + (e.key === "ArrowDown" ? 1 : -1) + suggList.length) % suggList.length;
    paintSuggSel();
    return;
  }
  if (e.key === "Escape") { closeFind(); return; }
  if (e.key === "Enter") { e.preventDefault(); void submitSearch(); }
});

async function locate(autoplay: boolean) {
  if (!navigator.geolocation) { fail(t(lang).noGeo); return; }
  el.here.disabled = true;
  navigator.geolocation.getCurrentPosition(
    async (pos) => {
      const { latitude: lat, longitude: lon } = pos.coords;
      el.here.disabled = false;
      // Тот же город — не перезапускаем то, что уже играет.
      if (lastObs && samePlace(place, { name: "", lat, lon })) return;
      const name = (await reverseGeocode(lat, lon, lang)) ?? t(lang).here_name;
      el.q.value = name;
      closeFind();
      void run({ name, lat, lon, nameLang: lang }, autoplay);
    },
    () => { el.here.disabled = false; fail(t(lang).geoDenied); },
    { timeout: 9000, maximumAge: 300000 },
  );
}
el.here.addEventListener("click", () => void locate(true));

async function recheck() {
  if (!profile || !lastObs) return;
  try {
    const obs = await fetchWeather(place);
    lastObs = obs;
    const fresh = toSignals(obs);
    const next = toProfile(fresh);
    const before = queryTags(profile, 3).join("+");
    signals = fresh;
    profile = next;
    paintWeather(obs);
    if (before !== queryTags(next, 3).join("+")) {
      drift = 0;
      set = extendSet(set.slice(0, idx + 1),
        buildSet(rank(await fetchCandidates(next, { rotation }), next, memory()), 12, pickOpts()), 8);
      renderQueue();
    }
  } catch { /* следующая попытка через интервал */ }
}

/* ───────────────────────── установка приложением ───────────────────────── */

let installEvent: (Event & { prompt: () => Promise<void> }) | null = null;
const INSTALL_SNOOZE = "pw.install.no";

/**
 * Уже установлено — предлагать установку нельзя.
 *
 * Одной проверки display-mode мало: манифест просит fullscreen, но Android
 * может отдать standalone или minimal-ui по цепочке отката, а Chrome в
 * установленном приложении всё равно иногда шлёт beforeinstallprompt.
 * Поэтому смотрим все режимы сразу, флаг iOS, метку из start_url и переход
 * из android-app://.
 */
function isInstalled(): boolean {
  const modes = ["standalone", "fullscreen", "minimal-ui", "window-controls-overlay"];
  if (modes.some((m) => matchMedia(`(display-mode: ${m})`).matches)) return true;
  if ((navigator as { standalone?: boolean }).standalone) return true;
  if (document.referrer.startsWith("android-app://")) return true;
  try {
    if (new URLSearchParams(location.search).get("src") === "pwa") return true;
  } catch { /* нечего разбирать */ }
  return false;
}

function mayOfferInstall(): boolean {
  if (isInstalled()) return false;
  try {
    const until = Number(localStorage.getItem(INSTALL_SNOOZE) || 0);
    return Date.now() > until;
  } catch { return true; }
}

window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  installEvent = e as Event & { prompt: () => Promise<void> };
});

/**
 * Предлагаем установку не по таймеру, а когда человек реально слушает: минута
 * проигранного — это интерес, а всплывшее на второй секунде — назойливость.
 */
let listened = 0;
let lastTick = 0;
el.audio.addEventListener("timeupdate", () => {
  const now = Date.now();
  if (lastTick && now - lastTick < 2000) listened += now - lastTick;
  lastTick = now;
  if (listened > 60_000 && installEvent && el.install.hidden && mayOfferInstall()) {
    el.install.hidden = false;
  }
});

el.installYes.addEventListener("click", async () => {
  el.install.hidden = true;
  if (!installEvent) return;
  await installEvent.prompt();
  installEvent = null;
});
el.installNo.addEventListener("click", () => {
  el.install.hidden = true;
  // Отказ помнится две недели — предлагать снова завтра было бы навязчиво.
  try { localStorage.setItem(INSTALL_SNOOZE, String(Date.now() + 14 * 864e5)); } catch { /* приватный режим */ }
});
window.addEventListener("appinstalled", () => {
  el.install.hidden = true;
  installEvent = null;
  try { localStorage.setItem(INSTALL_SNOOZE, String(Date.now() + 3650 * 864e5)); } catch { /* приватный режим */ }
});

/* ───────────────────────── старт ───────────────────────── */

el.brandIc.innerHTML = ICONS["cloud-sun"] ?? "";
el.findIc.innerHTML = ICONS.search ?? "";
el.langIc.innerHTML = ICONS.lang ?? "";
el.installIc.innerHTML = ICONS.install ?? "";
el.here.innerHTML = icon("pin");
el.prev.innerHTML = icon("prev");
el.next.innerHTML = icon("next");
el.play.innerHTML = icon("play");

try {
  const v = localStorage.getItem("pw.vol");
  if (v !== null) el.audio.volume = Math.min(1, Math.max(0, Number(v)));
} catch { /* приватный режим */ }
paintVol();

/**
 * Словарь приезжает до первой отрисовки, иначе на секунду мелькнёт
 * английский. Английский вшит статически, поэтому ждать нечего, когда
 * выбран он.
 */
async function boot() {
  await loadDict(lang);
  paintLang();
  void fetchCitiesNow().then((c) => { cityData = c; renderCities(); }).catch(() => {});

  if (navigator.permissions?.query) {
    navigator.permissions.query({ name: "geolocation" as PermissionName })
      .then((s) => { if (s.state === "granted") void locate(true); else void run(startPlace()); })
      .catch(() => void run(startPlace()));
  } else {
    void run(startPlace());
  }
}

// Вкладка не видна — движение останавливается. Звук при этом продолжается:
// приложение должно спокойно играть свёрнутым.
document.addEventListener("visibilitychange", () => {
  document.body.dataset.hidden = document.hidden ? "1" : "0";
});

// Вкладку закрыли или свернули насовсем — иначе вердикт по последнему треку
// потерялся бы, и самое свежее прослушивание не попало бы в историю.
// pagehide, а не beforeunload: на мобильных только он и срабатывает.
window.addEventListener("pagehide", closeCurrent);

setInterval(() => { if (lastObs) void recheck(); }, RECHECK_MS);

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").catch(() => { /* не критично */ });
  });
}

const startPlace = (): Place =>
  lastPlace() ?? { name: lang === "ru" ? HOME.ru : HOME.en, lat: HOME.lat, lon: HOME.lon };

void boot();
